// ============================================
// API Provider - Context for API access
// ============================================

import { createContext, useContext, type ReactNode } from 'react';
import { api, tokenStorage } from '../config/api';
import type {
  AuditAction,
  AuditLog,
  PaginatedResponse,
  RestrictedSalesListResponse,
  RestrictedSalesParams,
} from '@conai/shared-types';

type SupplierEntity = {
  id: string;
  code: string;
  name: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  address?: string | null;
  payment_method: string;
  payment_terms: string;
  lead_time_days: number;
  status: 'active' | 'inactive' | 'pending';
  is_active: boolean;
};

type SuppliersServiceLike = {
  list: (params?: unknown) => Promise<PaginatedResponse<SupplierEntity>>;
  getById: (id: string) => Promise<SupplierEntity>;
  search: (query: string, params?: unknown) => Promise<PaginatedResponse<SupplierEntity>>;
  getActive: (params?: unknown) => Promise<PaginatedResponse<SupplierEntity>>;
  create: (data: unknown) => Promise<SupplierEntity>;
  update: (id: string, data: unknown) => Promise<SupplierEntity>;
  delete: (id: string) => Promise<void>;
  activate: (id: string) => Promise<SupplierEntity>;
  deactivate: (id: string) => Promise<SupplierEntity>;
};

type GeneralSettingsLike = {
  store_name: string;
  store_email: string;
  timezone: string;
  currency: string;
  language: string;
};

type NotificationSettingsLike = {
  email_notifications: boolean;
  low_stock_alerts: boolean;
  order_notifications: boolean;
  daily_reports: boolean;
  weekly_digest: boolean;
};

type SecuritySettingsLike = {
  two_factor_enabled: boolean;
  session_timeout: number;
  password_expiry: number;
  ip_whitelist: string;
};

type SettingsServiceLike = {
  getAll: () => Promise<unknown>;
  getGeneral: () => Promise<GeneralSettingsLike>;
  getNotifications: () => Promise<NotificationSettingsLike>;
  getSecurity: () => Promise<SecuritySettingsLike>;
  getIntegrations: () => Promise<Array<{ name: string; description: string; status: 'connected' | 'disconnected' | 'pending' }>>;
  getStats: () => Promise<unknown>;
  updateGeneral: (data: unknown) => Promise<GeneralSettingsLike>;
  updateNotifications: (data: unknown) => Promise<NotificationSettingsLike>;
  updateSecurity: (data: unknown) => Promise<SecuritySettingsLike>;
  updateIntegration: (name: string, config: Record<string, unknown>) => Promise<{ name: string }>;
  connectIntegration: (name: string) => Promise<{ name: string }>;
  disconnectIntegration: (name: string) => Promise<{ name: string }>;
  resetToDefaults: () => Promise<unknown>;
};

type AuditLogsServiceLike = {
  list: (params?: unknown) => Promise<PaginatedResponse<AuditLog>>;
  getById: (id: string) => Promise<AuditLog>;
  getStats: () => Promise<{
    total_logs: number;
    today_activity: number;
    unique_users: number;
    actions_by_type: Partial<Record<AuditAction, number>>;
  }>;
  getByAction: (action: string, params?: unknown) => Promise<PaginatedResponse<AuditLog>>;
  getByEntityType: (entityType: string, params?: unknown) => Promise<PaginatedResponse<AuditLog>>;
  getByUser: (userId: string, params?: unknown) => Promise<PaginatedResponse<AuditLog>>;
  getByEntity: (entityType: string, entityId: string, params?: unknown) => Promise<PaginatedResponse<AuditLog>>;
  getToday: (params?: unknown) => Promise<PaginatedResponse<AuditLog>>;
  getRecentActivity: (limit?: number) => Promise<AuditLog[]>;
  getAuthHistory: (userId: string) => Promise<PaginatedResponse<AuditLog>>;
  // Sổ bằng chứng bán hàng 18+ — envelope {data, meta}, không phải {count, results}
  listRestrictedSales: (params?: RestrictedSalesParams) => Promise<RestrictedSalesListResponse>;
  exportRestrictedSales: (params?: RestrictedSalesParams) => Promise<Blob>;
};

interface ApiContextValue {
  api: typeof api;
}

const ApiContext = createContext<ApiContextValue | null>(null);

interface ApiProviderProps {
  children: ReactNode;
}

export function ApiProvider({ children }: ApiProviderProps) {
  const value: ApiContextValue = {
    api,
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

/**
 * Hook to access the API client
 */
export function useApi(): typeof api {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within ApiProvider');
  }
  return context.api;
}

/**
 * Hook to access individual API services
 */
export function useAuthService() {
  return useApi().auth;
}

export function useUsersService() {
  return useApi().users;
}

export function useStoresService() {
  return useApi().stores;
}

export function useCategoriesService() {
  return useApi().categories;
}

export function useProductsService() {
  return useApi().products;
}

export function useInventoryService() {
  return useApi().inventory;
}

export function useStockImportsService() {
  return useApi().stockImports;
}

export function useOrdersService() {
  return useApi().orders;
}

export function usePaymentsService() {
  return useApi().payments;
}

export function useCustomersService() {
  return useApi().customers;
}

export function useLoyaltyService() {
  return useApi().loyalty;
}

export function useAccessControlService() {
  return useApi().accessControl;
}

export function useAtWorkService() {
  return useApi().atWork;
}

export function usePromotionsService() {
  return useApi().promotions;
}

export function useReportsService() {
  return useApi().reports;
}

export function useSuppliersService() {
  const apiInstance = useApi() as typeof api & {
    suppliers?: SuppliersServiceLike;
    client: {
      get: <T>(url: string, params?: Record<string, string | number | boolean | undefined>) => Promise<T>;
      post: <T, D>(url: string, data: D) => Promise<T>;
      put: <T, D>(url: string, data: D) => Promise<T>;
      delete: <T>(url: string) => Promise<T>;
    };
  };

  if (apiInstance.suppliers) {
    return apiInstance.suppliers;
  }

  // Fallback when bundled ConAIApi type/runtime is outdated
  return {
    list: (params: unknown) =>
      apiInstance.client.get<PaginatedResponse<SupplierEntity>>(
        '/suppliers/',
        params as Record<string, string | number | boolean | undefined>
      ),
    getById: (id: string) => apiInstance.client.get<SupplierEntity>(`/suppliers/${id}/`),
    search: (query: string, params: unknown) =>
      apiInstance.client.get<PaginatedResponse<SupplierEntity>>('/suppliers/', {
        ...(params as Record<string, string | number | boolean | undefined>),
        search: query,
      }),
    getActive: (params: unknown) =>
      apiInstance.client.get<PaginatedResponse<SupplierEntity>>('/suppliers/', {
        ...(params as Record<string, string | number | boolean | undefined>),
        is_active: true,
      }),
    create: (data: unknown) => apiInstance.client.post<SupplierEntity, unknown>('/suppliers/', data),
    update: (id: string, data: unknown) =>
      apiInstance.client.put<SupplierEntity, unknown>(`/suppliers/${id}/`, data),
    delete: (id: string) => apiInstance.client.delete(`/suppliers/${id}/`),
    activate: (id: string) =>
      apiInstance.client.post<SupplierEntity, Record<string, never>>(`/suppliers/${id}/activate/`, {}),
    deactivate: (id: string) =>
      apiInstance.client.post<SupplierEntity, Record<string, never>>(`/suppliers/${id}/deactivate/`, {}),
  };
}

export function useSettingsService() {
  return (useApi() as typeof api & { settings: SettingsServiceLike }).settings;
}

export function useAuditLogsService() {
  return (useApi() as typeof api & { auditLogs: AuditLogsServiceLike }).auditLogs;
}

export function useNotificationsService() {
  return useApi().notifications;
}

/** Hoá đơn điện tử — sheet 2 mục #51. Schema BE chốt 29/08/2026. */
export function useReceiptsService() {
  return useApi().receipts;
}

/** Hàng chờ khiếu nại hoá đơn — sheet 2 mục #58/#60. */
export function useDisputesService() {
  return useApi().disputes;
}

/** Đổi/trả hàng phía nhân viên — sheet 2 mục #55/#56/#59. */
export function useReturnsService() {
  return useApi().returns;
}

// Re-export for external use
export { tokenStorage };
